import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { toast } from "sonner";
import type {
  RecipeApiItem,
  RecipeIngredient,
  RecipeStep,
  RecipeTaxonomyRefs,
} from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import { Textarea } from "@/ui/textarea.js";
import { useImport } from "../../features/imports/useImport.js";
import { useRecipe } from "../../features/recipes/useRecipe.js";
import { useAuth } from "../../features/auth/AuthProvider.js";
import { sync } from "../../sync/SyncService.js";
import { IngredientList } from "../../features/recipes/IngredientList.js";
import { StepList } from "../../features/recipes/StepList.js";
import { TaxonomyPicker } from "../../features/recipes/TaxonomyPicker.js";
import { PhotoUploader } from "../../features/recipes/PhotoUploader.js";
import { RecipePhotoImg } from "../../features/recipes/RecipePhotoImg.js";

const EMPTY_TAXONOMY: RecipeTaxonomyRefs = {
  tagIds: [],
  categoryIds: [],
  cuisineId: null,
  dietFlags: [],
};

function toIntOrNull(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Photo-import review (Slice D): the source page photo(s) pinned beside the
 * editable extracted fields, an uncertainty banner, and a Confirm that flips the
 * hidden `pending` draft to `active` (entering the library). Route param `id` is
 * the import JOB id (warnings live on the job).
 */
export function PhotoReview(): JSX.Element {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { me } = useAuth();
  const householdId =
    me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  const { data: job, status: jobStatus } = useImport(jobId);
  const recipeId = job?.recipeId ?? undefined;
  const { recipe, status: recipeStatus } = useRecipe(recipeId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [servings, setServings] = useState("");
  const [totalTimeMin, setTotalTimeMin] = useState("");
  const [activeTimeMin, setActiveTimeMin] = useState("");
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [steps, setSteps] = useState<RecipeStep[]>([]);
  const [taxonomy, setTaxonomy] = useState<RecipeTaxonomyRefs>(EMPTY_TAXONOMY);
  const [version, setVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [liveRecipe, setLiveRecipe] = useState<RecipeApiItem | null>(null);

  // Hydrate the form once per recipe; afterward only keep the optimistic-
  // concurrency version fresh (a cover upload bumps it) — never clobber edits.
  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (recipeStatus !== "ready" || !recipe) return;
    if (hydratedRef.current === recipe.id) {
      setVersion((p) => (p !== null && recipe.version > p ? recipe.version : p));
      setLiveRecipe((prev) => (prev && prev.version >= recipe.version ? prev : recipe));
      return;
    }
    hydratedRef.current = recipe.id;
    setTitle(recipe.title);
    setDescription(recipe.description ?? "");
    setServings(recipe.servings != null ? String(recipe.servings) : "");
    setTotalTimeMin(recipe.totalTimeMin != null ? String(recipe.totalTimeMin) : "");
    setActiveTimeMin(recipe.activeTimeMin != null ? String(recipe.activeTimeMin) : "");
    setIngredients(recipe.ingredients);
    setSteps(recipe.steps);
    setTaxonomy(recipe.taxonomy);
    setVersion(recipe.version);
    setLiveRecipe(recipe);
  }, [recipe, recipeStatus]);

  if (!jobId) return <p className="p-6 text-destructive">Missing import id.</p>;
  if (jobStatus === "loading" || recipeStatus === "loading") {
    return <p className="p-6 text-muted-foreground">Loading…</p>;
  }
  if (!recipeId || recipeStatus === "not-found") {
    return (
      <div className="p-6 text-center space-y-2">
        <p className="text-destructive">We couldn't find the extracted recipe.</p>
        <Link to="/imports/photo" className="underline text-primary">
          Try photographing it again
        </Link>
      </div>
    );
  }

  const warnings = job?.warnings ?? [];
  const pagePhotos = liveRecipe?.photos.extras ?? [];
  const canSave = title.trim().length > 0 && version !== null && !saving;

  async function onConfirm(): Promise<void> {
    if (!canSave || version === null || !recipeId) return;
    setSaving(true);
    try {
      const cleanIngredients = ingredients
        .filter((i) => i.raw.trim().length > 0)
        .map((i) => {
          const raw = i.raw.trim();
          return { ...i, raw, name: (i.name ?? "").trim() || raw };
        });
      const cleanSteps = steps
        .filter((s) => s.text.trim().length > 0)
        .map((s) => ({ ...s, text: s.text.trim() }));
      await sync.patchRecipeOptimistic(recipeId, {
        version,
        status: "active",
        title: title.trim(),
        ingredients: cleanIngredients,
        steps: cleanSteps,
        taxonomy,
        description: description.trim() || null,
        servings: toIntOrNull(servings),
        totalTimeMin: toIntOrNull(totalTimeMin),
        activeTimeMin: toIntOrNull(activeTimeMin),
      });
      toast.success("Recipe saved to your library");
      await navigate(`/recipes/${encodeURIComponent(recipeId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the recipe");
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Review extracted recipe</h1>
        <p className="text-sm text-muted-foreground">
          Check it against the source photo, fix anything, then confirm to add it to your library.
        </p>
      </div>

      {warnings.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        >
          <p className="font-medium">Double-check these:</p>
          <ul className="list-disc pl-5 mt-1 space-y-0.5 text-muted-foreground">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Source pages — pinned above the fields on mobile, beside on desktop. */}
        <section aria-label="Source pages" className="space-y-2 md:sticky md:top-4 md:self-start">
          <span className="text-sm text-muted-foreground">Source scan</span>
          {pagePhotos.length === 0 ? (
            <p className="text-sm text-muted-foreground">No source photo available.</p>
          ) : (
            <div className="space-y-3">
              {pagePhotos.map((photoKey) => (
                <RecipePhotoImg
                  key={photoKey}
                  householdId={householdId}
                  recipeId={recipeId}
                  photoKey={photoKey}
                  size="full"
                  alt="Source recipe page"
                  className="w-full rounded-md border object-contain max-h-[70vh]"
                />
              ))}
            </div>
          )}
          {liveRecipe && (
            <div className="pt-2">
              <PhotoUploader
                householdId={householdId}
                recipe={liveRecipe}
                onRecipeChange={setLiveRecipe}
              />
            </div>
          )}
        </section>

        {/* Editable extracted fields. */}
        <section aria-label="Extracted fields" className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="pr-title">Title</Label>
            <Input id="pr-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="pr-description">Description</Label>
            <Textarea
              id="pr-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pr-servings">Servings</Label>
              <Input
                id="pr-servings"
                inputMode="numeric"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pr-total">Total min</Label>
              <Input
                id="pr-total"
                inputMode="numeric"
                value={totalTimeMin}
                onChange={(e) => setTotalTimeMin(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pr-active">Active min</Label>
              <Input
                id="pr-active"
                inputMode="numeric"
                value={activeTimeMin}
                onChange={(e) => setActiveTimeMin(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Ingredients</Label>
            <IngredientList value={ingredients} onChange={setIngredients} />
          </div>

          <div className="space-y-1">
            <Label>Steps</Label>
            <StepList value={steps} onChange={setSteps} />
          </div>

          <TaxonomyPicker value={taxonomy} onChange={setTaxonomy} />
        </section>
      </div>

      <div className="flex flex-wrap gap-3 border-t pt-4">
        <Button
          type="button"
          disabled={!canSave}
          onClick={() => {
            void onConfirm();
          }}
        >
          {saving ? "Saving…" : "Confirm & save"}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => {
            void navigate("/imports/photo");
          }}
        >
          Retake / add page
        </Button>
      </div>
    </div>
  );
}
